import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, finalize, of, switchMap, tap } from 'rxjs';
import { DateTime } from 'luxon';

import {
  DateRange,
  encodeApiError,
  TApiErrorResponse,
} from '@sandbox-platform/shared-ui';

import { CommissionPaymentApiService } from '../../../../services/commission-payment-api.service';
import {
  clonePaymentListFilters,
  CommissionPaymentDTO,
  CommissionPaymentListFilters,
  defaultPaymentListFilters,
} from '../../../../types/commission-payment.types';
import { encodePaymentSearchParams } from './definitions';

interface CommissionPaymentListState {
  list: CommissionPaymentDTO[];
  total: number;
  currentPage: number;
  pageSize: number;
  loading: boolean;
  error: string | TApiErrorResponse | null;
  selectedDateRange: DateRange;
  filter: CommissionPaymentListFilters;
  sortBy: string;
  sortDirection: 'asc' | 'desc';
}

const initialState: CommissionPaymentListState = {
  list: [],
  total: 0,
  currentPage: 1,
  pageSize: 20,
  loading: false,
  error: null,
  selectedDateRange: {
    from: DateTime.now().minus({ months: 3 }).startOf('day').toJSDate(),
    to: DateTime.now().endOf('day').toJSDate(),
  },
  filter: defaultPaymentListFilters(),
  sortBy: 'payment_date',
  sortDirection: 'desc',
};

export const CommissionPaymentListStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed((store) => ({
    hasNextPage: computed(
      () => store.currentPage() < Math.ceil(store.total() / store.pageSize())
    ),
    payments: computed(() => store.list()),
    isLoading: computed(() => store.loading()),
  })),
  withMethods((store, apiService = inject(CommissionPaymentApiService)) => {
    const buildParams = (page: number) =>
      encodePaymentSearchParams(
        page,
        store.pageSize(),
        store.selectedDateRange(),
        store.filter(),
        store.sortBy(),
        store.sortDirection()
      );

    return {
      get: rxMethod<void>(
        switchMap(() => {
          patchState(store, { loading: true, error: null, currentPage: 1 });
          const {
            page,
            limit,
            paymentType,
            paymentMethod,
            payeeOrganizationId,
            invoiceRecipientOrganizationId,
            dateFrom,
            dateTo,
            sortBy,
            sortDirection,
          } = buildParams(1);

          return apiService
            .list(
              undefined,
              page,
              limit,
              paymentType,
              paymentMethod,
              payeeOrganizationId,
              invoiceRecipientOrganizationId,
              dateFrom,
              dateTo,
              sortBy,
              sortDirection
            )
            .pipe(
              tap((response) => {
                if (response?.data) {
                  patchState(store, {
                    list: response.data.items ?? [],
                    total: response.data.total,
                    currentPage: response.data.page,
                    pageSize: response.data.limit,
                  });
                }
              }),
              catchError((error) => {
                patchState(store, {
                  error: encodeApiError(error, 'Failed to load payments'),
                });
                return of(null);
              }),
              finalize(() => patchState(store, { loading: false }))
            );
        })
      ),

      loadMore: rxMethod<void>(
        switchMap(() => {
          if (store.loading() || !store.hasNextPage()) {
            return of(null);
          }

          patchState(store, { loading: true, error: null });
          const {
            page,
            limit,
            paymentType,
            paymentMethod,
            payeeOrganizationId,
            invoiceRecipientOrganizationId,
            dateFrom,
            dateTo,
            sortBy,
            sortDirection,
          } = buildParams(store.currentPage() + 1);

          return apiService
            .list(
              undefined,
              page,
              limit,
              paymentType,
              paymentMethod,
              payeeOrganizationId,
              invoiceRecipientOrganizationId,
              dateFrom,
              dateTo,
              sortBy,
              sortDirection
            )
            .pipe(
              tap((response) => {
                if (response?.data) {
                  patchState(store, {
                    list: [...store.list(), ...(response.data.items ?? [])],
                    total: response.data.total,
                    currentPage: response.data.page,
                    pageSize: response.data.limit,
                  });
                }
              }),
              catchError((error) => {
                patchState(store, {
                  error: encodeApiError(error, 'Failed to load payments'),
                });
                return of(null);
              }),
              finalize(() => patchState(store, { loading: false }))
            );
        })
      ),

      setDateRange(from: Date | null, to: Date | null): void {
        patchState(store, {
          selectedDateRange: { from, to },
          currentPage: 1,
        });
        this.get();
      },

      onFromDateChange(event: Event): void {
        const target = event.target as HTMLInputElement;
        const fromDate = target.value ? new Date(target.value) : null;
        const currentRange = store.selectedDateRange();
        this.setDateRange(fromDate, currentRange.to);
      },

      onToDateChange(event: Event): void {
        const target = event.target as HTMLInputElement;
        const toDate = target.value ? new Date(target.value) : null;
        const currentRange = store.selectedDateRange();
        this.setDateRange(currentRange.from, toDate);
      },

      setSort(sortBy: string, sortDirection: 'asc' | 'desc'): void {
        patchState(store, { sortBy, sortDirection, currentPage: 1 });
        this.get();
      },

      setFilters(filters: CommissionPaymentListFilters): void {
        patchState(store, {
          filter: clonePaymentListFilters(filters),
          currentPage: 1,
        });
        this.get();
      },

      clearError(): void {
        patchState(store, { error: null });
      },
    };
  })
);

export type CommissionPaymentListStoreType = InstanceType<
  typeof CommissionPaymentListStore
>;
